import { usePhrase } from '@/hooks';
import { Stack, Typography } from '@mui/material';
import { Tooltip } from '@/components/forms/other/Tooltip/Tooltip';
import { WalletPaymentIcon } from '@/components/blocks/wallet';
import { formatDistance, subDays } from 'date-fns';
import { isEmpty } from '@/utils/fp';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';
import { WalletMinMaxLabel } from '@/components/blocks/wallet/Payment/WalletMinMaxLabel/WalletMinMaxLabel';

import { ClockIcon } from '@/assets/theme/default/walletIcons/clock/ClockIcon';

import styles from './WalletRadioCustomLabel.module.css';

interface WalletRadioCustomLabelProps {
    paymentMethodId: number;
    phraseId: number;
    descriptionPhraseId?: number;
    completeDt?: string;
    summary?: {
        phraseId: number;
        id: string;
        value: string;
    }[];
    isComingSoon?: boolean;
    amountMin?: string;
    amountMax?: string;
    currencySymbol?: string;
    currencyCode?: string;
    currencyPosition?: string;
}

export const WalletRadioCustomLabel = ({
    paymentMethodId,
    phraseId,
    descriptionPhraseId,
    completeDt,
    summary,
    isComingSoon,
    amountMax,
    amountMin,
    currencySymbol,
    currencyCode,
    currencyPosition,
}: WalletRadioCustomLabelProps) => {
    const { t } = usePhrase();
    return (
        <>
            {isComingSoon && (
                <Stack
                    direction="row"
                    className={`${styles.comingSoonLabel} paymentMethodComingSoonLabel`}
                    sx={{
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <ClockIcon />
                    {t(GENERAL_PHRASES.COMING_SOON)}
                </Stack>
            )}
            <Stack
                direction="row"
                spacing={1}
                className={`${styles.paymentTitle} paymentMethodTitle`}
                sx={{
                    alignItems: 'center',
                }}
            >
                <WalletPaymentIcon id={paymentMethodId} />
                <span>{t(phraseId)}</span>
                {completeDt && (
                    <Tooltip title={new Date(completeDt).toLocaleString()} enterTouchDelay={0}>
                        <Typography variant="caption" className="paymentDate">
                            {formatDistance(subDays(new Date(completeDt), 0), new Date(), {
                                addSuffix: true,
                            })}
                        </Typography>
                    </Tooltip>
                )}
            </Stack>
            {summary && !isEmpty(summary) ? (
                <Typography
                    variant="caption"
                    className={`${styles.paymentDescription} paymentDescription`}
                >
                    <Stack sx={{ flexDirection: ['column', 'row'], flexWrap: 'wrap', gap: [0, 2] }}>
                        {summary.map(({ phraseId, value, id }) => (
                            <span key={phraseId} style={{ marginTop: 0 }}>
                                {id === 'firstName' || id === 'lastName'
                                    ? value
                                    : `${t(phraseId)}: ${value}`}
                            </span>
                        ))}
                    </Stack>
                </Typography>
            ) : (
                <Typography
                    variant="caption"
                    className={`${styles.paymentDescription} paymentDescription`}
                >
                    {t(descriptionPhraseId)}
                </Typography>
            )}
            <WalletMinMaxLabel
                amountMax={amountMax ? parseInt(amountMax) : undefined}
                amountMin={amountMin ? parseInt(amountMin) : undefined}
                currencySymbol={currencySymbol}
                currencyCode={currencyCode ? currencyCode : ''}
                currencyPosition={currencyPosition}
            />
        </>
    );
};
